import { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  useActiveAccountId,
  useActiveProjectCohortId,
  useNotify,
  useServiceAuth,
} from '../../../../hooks';
import FhirService from '../../../../services/FhirService';
import {
  getIsFavorite,
  removeFavoriteSystem,
  upsertFavoriteSystem,
} from './util';
import axios from 'axios';
import sleep from '../../../common/util/sleep';

const messages = defineMessages({
  favoriteError: {
    id: 'SubjectViewer.Favorite.favoriteError',
    defaultMessage: 'Error occurred updating favorite',
  },
});

export type UseFavoriteArgs = {
  resource: fhir.DomainResource;
  onFavoriteUpdate?: (resource: fhir.DomainResource) => void;
  removeFavorite?: (resource: fhir.DomainResource) => fhir.DomainResource;
  addFavorite?: (resource: fhir.DomainResource) => fhir.DomainResource;
  isFavorite?: (resource: fhir.DomainResource) => boolean;
};

export type UseFavoriteReturnValue = {
  onFavorite: () => void;
  onUnfavorite: () => void;
  isFavorite: boolean;
};

const fhirService = new FhirService();

/**
 * Persist across unmounts of the component.
 *
 * Theoretically, if the user keeps the app open for a while and another user makes an update, the favorite status
 * could go stale.  This seems highly unlikely, and if it does happen, a timeout for cached data could be implemented.
 */
const inMemoryResources: Record<string, fhir.DomainResource> = {};

type UpdaterFn = (newResource: fhir.DomainResource) => fhir.DomainResource;

export const useFavorite = ({
  resource,
  onFavoriteUpdate,
  removeFavorite = removeFavoriteSystem,
  addFavorite = upsertFavoriteSystem,
  isFavorite = getIsFavorite,
}: UseFavoriteArgs): UseFavoriteReturnValue => {
  // keep internal cache of resource for optimistic updates
  const [cachedResource, setCachedResource] = useState<fhir.DomainResource>(
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    inMemoryResources[resource.id] || resource,
  );
  const { formatMessage } = useIntl();
  const serviceAuth = useServiceAuth();
  const activeAccountId = useActiveAccountId();
  const [notify] = useNotify();
  const activeCohortId = useActiveProjectCohortId();

  const updateResource = (updater: UpdaterFn, retries = 5): void => {
    const oldResource = inMemoryResources[resource?.id || ''] || resource;
    const newResource = updater(oldResource);
    setCachedResource(newResource);
    const { account, ...requestData } = newResource as fhir.DomainResource & {
      account: string;
    };

    fhirService
      .openPut(serviceAuth)({
        account: activeAccountId,
        resourceType: resource.resourceType as any,
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        id: resource.id,
        updatedResource: requestData,
        cohortId: activeCohortId,
        confirmNoConflict: true,
      })
      .then(res => {
        const updatedResource = res.data as fhir.DomainResource;
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        inMemoryResources[resource.id] = updatedResource;
        if (onFavoriteUpdate) {
          onFavoriteUpdate(updatedResource);
        }
      })
      .catch(e => {
        const canRetry = retries > 0;
        if (canRetry && axios.isAxiosError(e) && e.response?.status === 409) {
          return sleep(300).then(() => {
            updateResource(updater, retries - 1);
          });
        }
        console.error(e);
        notify(formatMessage(messages.favoriteError), 'error');
        setCachedResource(oldResource);
      });
  };

  return {
    onFavorite: () => {
      updateResource(addFavorite);
    },
    onUnfavorite: () => {
      updateResource(removeFavorite);
    },
    isFavorite: isFavorite(cachedResource),
  };
};
